import React, { useEffect, useState } from "react";
import {
  GetCustomerSupport,
  GetViolationReportFile,
  GetViolationsReports,
} from "Services/OtherApis";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { isArabic } from "constants/isArabic";
import moment from "moment";
import { IoEyeOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";

function ViolationsReport() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [language, setSelectedLanguage] = useState(isRTL ? "ar" : "en");
  const [loadingReports, setLoadingDownloadReport] = useState({}); // Loading state for downloads
  const [loadingPreviews, setLoadingPreviews] = useState({}); // Loading state for previews
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getViolationsReportData();
  }, []);

  async function getViolationsReportData(value) {
    setLoading(true);
    try {
      const res = await GetViolationsReports(language);
      setData(res?.data || []); // Ensure data is always an array
    } catch (e) {
      console.error("Error fetching jobs:", e);
    } finally {
      setLoading(false);
    }
  }

  const exportToExcel = () => {
    const headers = ["Name", "Phone", "Date", "Nature Of Incident", "Message"];

    const formattedData = data.map(({ updatedAt, __v, _id, ...rest }) => ({
      Name: rest?.fullName,
      Phone: rest.phone,
      Date: moment(rest.createdAt).format("LLL"),
      "Nature Of Incident": rest.natureOfIncident,
      Message: rest.message,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData, {
      header: headers,
    });

    // Auto-adjust column widths based on content length
    const wscols = headers.map((header) => {
      const maxLength = formattedData.reduce((max, row) => {
        return row[header] ? Math.max(max, row[header].toString().length) : max;
      }, header.length);
      return { wch: maxLength + 5 }; // Adding padding
    });

    worksheet["!cols"] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Violation_Report");
    XLSX.writeFile(workbook, "Violation_Report.xlsx");
  };

  async function getReportVoilation(applicantId, isPreview = false, name) {
    if (isPreview) {
      setLoadingPreviews((prev) => ({ ...prev, [applicantId]: true })); // Start preview loading
    } else {
      setLoadingDownloadReport((prev) => ({ ...prev, [applicantId]: true })); // Start download loading
    }

    try {
      const res = await GetViolationReportFile(applicantId);
      const reportData = res?.file;

      if (!reportData) {
        toast.error("No Report file available");
        return;
      }

      if (isPreview) {
        openReportInNewTab(reportData);
      } else {
        downloadVoilationReportFunction(reportData, `${name} report`);
      }
    } catch (e) {
      toast.error("Error fetching report");
    } finally {
      if (isPreview) {
        setLoadingPreviews((prev) => ({ ...prev, [applicantId]: false })); // Stop preview loading
      } else {
        setLoadingDownloadReport((prev) => ({ ...prev, [applicantId]: false })); // Stop download loading
      }
    }
  }
  const openReportInNewTab = (base64Data) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Detect MIME type
    let mimeType = "application/pdf";
    if (base64Data.includes("JVBER")) {
      mimeType = "application/pdf";
    } else {
      mimeType =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    }

    const blob = new Blob([byteArray], { type: mimeType });
    const fileURL = URL.createObjectURL(blob);

    window.open(fileURL, "_blank");
  };
  const downloadVoilationReportFunction = (base64Data, fileName) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Detect MIME type
    let mimeType = "application/pdf";
    let extension = "pdf";
    if (base64Data.includes("JVBER")) {
      mimeType = "application/pdf";
      extension = "pdf";
    } else {
      mimeType =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      extension = "docx";
    }

    const blob = new Blob([byteArray], { type: mimeType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-xl  tracking-wide">
          {t("Violation Reports")}
        </div>
        {data?.length > 0 && (
          <button
            onClick={exportToExcel}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600"
          >
            {t("Export to Excel")}
          </button>
        )}
      </div>
      {loading ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300  py-24 text-lg rounded-md">
          {t("Loading...")}
        </p>
      ) : data?.length === 0 ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300  py-24 text-lg rounded-md">
          {t("No Data Available")} !
        </p>
      ) : (
        <div
          className={`overflow-x-auto relative  mt-4 bg-white p-3 rounded-lg `}
        >
          <table
            className={`w-full whitespace-nowrap  text-sm   ${
              isRTL ? "text-right" : "text-left"
            }`}
          >
            <thead className="text-md text-gray-500  bg-slate-100  font-normal">
              <th className=" px-4 py-4">{t("Name")}</th>
              <th className=" px-4 py-4">{t("Phone")}</th>
              <th className=" px-4 py-4">{t("Date")}</th>
              <th className=" px-4 py-4">{t("Nature Of Incident")}</th>
              <th className=" px-4 py-4">{t("Message")}</th>
              <th className=" px-4 py-4">{t("File")}</th>
            </thead>
            <tbody
              className={`text-gray-500 ${language === "ar" && "text-lg"}`}
            >
              {data?.map((applicant, k) => (
                <tr
                  key={applicant._id}
                  className={`odd:bg-white even:bg-gray-50 ${
                    k % 2
                      ? "bg-gray-50 dark:bg-gray-600"
                      : "bg-white dark:bg-gray-700"
                  }}`}
                >
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.fullName) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.fullName}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.phone) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.phone}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.createdAt) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {moment(applicant.createdAt).format("LLL")}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.natureOfIncident)
                        ? " Majalla text-lg"
                        : ""
                    }`}
                  >
                    {applicant.natureOfIncident}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 max-w-[800px] min-w-[500px] whitespace-normal break-normal  ${
                      isArabic(applicant.message) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant?.message}
                  </td>

                  <td className=" items-center border-b border-gray-300 px-4 py-4 flex flex-row  ">
                    <button
                      onClick={() => getReportVoilation(applicant?._id, true)}
                      className="text-gray-500 rounded-lg  cursor-pointer duration-300 hover:text-blue-600 flex items-center  w-10  "
                      disabled={loadingPreviews[applicant?._id]} // Disable only this button when clicked
                    >
                      {loadingPreviews[applicant?._id] ? (
                        <div className="flex items-center justify-center">
                          {/* <a>{t("Loading")} ... </a> */}

                          <svg
                            className="animate-spin h-7 w-7  text-white"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="white"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="white"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <div className="">
                          <a>
                            {" "}
                            <IoEyeOutline className="text-[25px]" />
                          </a>
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() =>
                        getReportVoilation(
                          applicant?._id,
                          false,
                          applicant.fullName
                        )
                      }
                      className="text-gray-500 rounded-lg  cursor-pointer duration-300 hover:text-blue-600 flex items-center  w-10 "
                      disabled={loadingReports[applicant?._id]} // Disable only this button when clicked
                    >
                      {loadingReports[applicant?._id] ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin h-7 w-7 mr-2 text-white"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="white"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="white"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <div className="">
                          <a>
                            {" "}
                            <MdOutlineFileDownload className="text-[28px] " />
                          </a>
                        </div>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ViolationsReport;
